import { apiClient } from './apiClient';
import type {
  AuditGeneralData,
  ProducerData,
  PlotData,
} from '../excel/types';

/**
 * Request payload for uploading audit data
 */
export interface UploadAuditDataRequest {
  audit_type: number;
  file_base64: string;
  audit_data: AuditGeneralData;
  producer_data: ProducerData;
  plots_data: PlotData[];
}

/**
 * Response from successful audit data upload
 */
export interface UploadAuditDataResponse {
  success: boolean;
  message: string;
  audit_id: number;
}

/**
 * Error response from audit data upload
 */
export interface UploadAuditDataError {
  success: false;
  errors: string[];
}

/**
 * Audit API Service
 * Handles all audit-related API operations
 */
export class AuditService {
  /**
   * Upload audit data from Excel file
   * 
   * @param auditId - The ID of the audit to update
   * @param data - The audit data extracted from Excel file
   * @returns Promise with the API response
   * 
   * @example
   * ```typescript
   * const response = await auditService.uploadAuditData(123, {
   *   audit_type: 1,
   *   file_base64: 'UEsDBBQABgAIAAAAIQD...',
   *   audit_data: { ... },
   *   producer_data: { ... },
   *   plots_data: [ ... ]
   * });
   * ```
   */
  async uploadAuditData(
    auditId: number,
    data: UploadAuditDataRequest
  ): Promise<UploadAuditDataResponse> {
    try {
      const response = await apiClient.post<UploadAuditDataResponse>(
        `/audits/${auditId}/upload-data/`,
        data
      );
      
      return response.data;
    } catch (error: any) {
      // Handle error response from backend. The endpoint may return:
      //   { errors: [str, str, …] }          ← service errors
      //   { errors: { field: [str, …], … } } ← DRF serializer errors
      //   { detail: "…" } / { message: "…" } ← fallbacks
      // Flatten everything to a string[] so the UI can show readable
      // multi-line errors instead of a generic "Error al cargar".
      //
      // The 5xx branch of the shared apiClient response interceptor
      // rejects with `{ message, originalError }` and strips
      // `error.response`, so we try the wrapper too — otherwise the
      // backend's `errors: [...]` payload (e.g. Excel validation
      // failures returned as HTTP 500) would never reach the UI and
      // the modal would only show "Server error" while the real
      // message lives in the network panel.
      const responseData =
        error?.response?.data ?? error?.originalError?.response?.data;
      if (responseData) {
        let errors: string[] = [];
        if (Array.isArray(responseData.errors)) {
          errors = responseData.errors.map((e: any) => String(e));
        } else if (responseData.errors && typeof responseData.errors === 'object') {
          for (const [field, val] of Object.entries(responseData.errors)) {
            if (Array.isArray(val)) {
              for (const msg of val) errors.push(`${field}: ${msg}`);
            } else {
              errors.push(`${field}: ${String(val)}`);
            }
          }
        }
        if (errors.length === 0) {
          errors = [
            responseData.message ||
              responseData.detail ||
              'Error al cargar datos de auditoría',
          ];
        }
        throw { success: false, errors } as UploadAuditDataError;
      }

      // Handle network or other errors
      throw {
        success: false,
        errors: [error.message || 'Error de conexión al cargar datos de auditoría'],
      } as UploadAuditDataError;
    }
  }
}

/**
 * Singleton instance of AuditService
 */
export const auditService = new AuditService();

export default auditService;
